// NotificationContext.tsx - Optimized
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getNofications } from "../utils/API";

interface NotificationContextType {
    notifications: any[];
    fetchNotifications: () => Promise<void>;
    setNotifications: React.Dispatch<React.SetStateAction<any[]>>;
    addNotification: (notification: any) => void;
    markAsRead: (notificationId: string) => void;
    unreadCount: number;
    markAsUnread: (notificationId: string) => void;
    isLoading: boolean; // Add loading state
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<any[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const getCurrentUserId = () => {
        try {
            const authData = JSON.parse(localStorage.getItem("authData") || "{}");
            return authData?.user?._id || authData?.user?.id || authData?._id || authData?.id || null;
        } catch {
            return null;
        }
    };

    // Memoized fetch function
    const fetchNotifications = useCallback(async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            setNotifications([]);
            setUnreadCount(0);
            return;
        }

        setIsLoading(true);
        try {
            const res = await getNofications();

            if (res?.notifications) {
                // Optimized: Direct update without complex merging
                const sortedNotifications = res.notifications.sort(
                    (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                );

                // Direct state update
                setNotifications(sortedNotifications);

                // Calculate unread count immediately
                const count = sortedNotifications.filter((n: any) => !n.read).length;
                setUnreadCount(count);
            }
        } catch (err) {
            console.error("Error fetching notifications", err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Optimized addNotification - update unreadCount immediately
    const addNotification = useCallback((notification: any) => {
        console.log("➕ Adding notification in real-time:", notification._id);
        const currentUserId = getCurrentUserId();
        const notificationUserId =
            typeof notification.userId === "object"
                ? notification.userId?._id?.toString()
                : notification.userId?.toString();

        if (currentUserId && notificationUserId && notificationUserId !== currentUserId.toString()) {
            return;
        }

        setNotifications(prev => {
            // Check if notification already exists (by id)
            if (prev.find(n => n._id === notification._id)) {
                return prev;
            }

            // Add to beginning
            const newNotifications = [notification, ...prev].slice(0, 100);
            return newNotifications;
        });

        // INSTANT unread count update
        setUnreadCount(prev => notification.read ? prev : prev + 1);
    }, []);

    // Optimized markAsRead - update unreadCount immediately
    const markAsRead = useCallback((notificationId: string) => {
        setNotifications(prev => {
            const updated = prev.map(n =>
                n._id === notificationId ? { ...n, read: true } : n
            );
            return updated;
        });

        // INSTANT unread count decrement
        setUnreadCount(prev => Math.max(0, prev - 1));
    }, []);

    const markAsUnread = useCallback((notificationId: string) => {
        setNotifications(prev => {
            const updated = prev.map(n =>
                n._id === notificationId ? { ...n, read: false } : n
            );
            return updated;
        });

        // INSTANT unread count increment
        setUnreadCount(prev => prev + 1);
    }, []);

    // Fetch notifications on mount
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                fetchNotifications,
                setNotifications,
                addNotification,
                markAsRead,
                markAsUnread,
                unreadCount,
                isLoading
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const ctx = useContext(NotificationContext);
    if (!ctx) throw new Error("useNotifications must be used inside NotificationProvider");
    return ctx;
};
